import { useSize } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useMemo, useRef } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineSubmit2 } from '@/app/components/icons/BaseLineSubmit2';
import { Loading } from '@/app/components/icons/Loading';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { FileDropZone } from '@/app/components/shared/FileDropZone';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { HeadlessImageUploader } from '@/app/components/shared/ImageUploader/HeadlessImageUploader';
import { MAX_IMAGE_FILE_SIZE } from '@/app/constants';
import type { ImageUploadFile } from '@/app/types';
import { cn } from '@/app/utils';
import { ToolMediaUploadHeader } from './ToolMediaUploadHeader';

interface ReferenceImageUploadProps {
  imageList: ImageUploadFile[];
  onImageUpload: (file: ImageUploadFile) => void;
  onRemoveImage: (fileId: string) => void;
  getSelectedImage: (url: string) => Promise<{ blob: Blob; width: number; height: number } | null>;
  title?: ReactNode;
  maxCount?: number;
  imageAccept?: string[];
  showOrder?: boolean;
  enableDrop?: boolean;
  className?: string;
}

export const MAX_REFERENCE_IMAGE_COUNT = 7;
const IMAGE_PREVIEW_MAX_WIDTH = 100;
const IMAGE_PREVIEW_GAP = 8;
const IMAGE_PREVIEW_HORIZONTAL_PADDING = 32;

export const MultiImageUpload: FC<ReferenceImageUploadProps> = ({
  imageList,
  onImageUpload,
  onRemoveImage,
  getSelectedImage,
  title,
  maxCount = MAX_REFERENCE_IMAGE_COUNT,
  imageAccept,
  showOrder = false,
  enableDrop = false,
  className,
}) => {
  const t = useTranslations();
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const previewContainerSize = useSize(previewContainerRef);

  const currentImageCount = imageList.length;
  const remainingImageSlots = Math.max(0, maxCount - currentImageCount);
  const canAddMore = remainingImageSlots > 0;
  const hasImages = currentImageCount > 0;
  const isNarrowContainer = (previewContainerSize?.width ?? Number.POSITIVE_INFINITY) < 360;
  const useMultiRowLayout = currentImageCount > 3 || (isNarrowContainer && currentImageCount > 2);
  const maxColumns = useMultiRowLayout ? (isNarrowContainer && currentImageCount === 3 ? 2 : 4) : currentImageCount;
  const previewRows = useMemo(() => {
    if (!hasImages) {
      return [];
    }

    if (!useMultiRowLayout) {
      return [imageList];
    }

    if (isNarrowContainer && currentImageCount === 3) {
      return [imageList.slice(0, 2), imageList.slice(2)];
    }

    return [imageList.slice(0, 4), imageList.slice(4)].filter((row) => row.length > 0);
  }, [currentImageCount, hasImages, imageList, isNarrowContainer, useMultiRowLayout]);
  const previewCardWidth = useMemo(() => {
    if (!hasImages || maxColumns <= 0) {
      return IMAGE_PREVIEW_MAX_WIDTH;
    }

    const containerWidth = previewContainerSize?.width ?? Number.POSITIVE_INFINITY;
    const availableWidth = Math.max(0, containerWidth - IMAGE_PREVIEW_HORIZONTAL_PADDING);
    const totalGapWidth = IMAGE_PREVIEW_GAP * Math.max(0, maxColumns - 1);
    const calculatedWidth = Math.floor((availableWidth - totalGapWidth) / maxColumns);

    return Math.min(IMAGE_PREVIEW_MAX_WIDTH, Math.max(0, calculatedWidth));
  }, [hasImages, maxColumns, previewContainerSize?.width]);

  const content = (
    <div className={cn('min-w-0 space-y-2', className)}>
      <ToolMediaUploadHeader
        icon={<ProductLinePic className="size-5 text-system-white" />}
        title={title ?? t('tools.videoRemake.imageUpload.label')}
      />

      <div
        ref={previewContainerRef}
        className="relative flex h-[140px] w-full flex-col items-center justify-center rounded-16 border border-system-white08 bg-system-white04 px-4 py-2 backdrop-blur-[30px]"
      >
        {hasImages ? (
          <div className="flex w-full max-w-[420px] flex-col items-center justify-center gap-2">
            {previewRows.map((row, rowIndex) => (
              <div key={`preview-row-${rowIndex + 1}`} className="flex w-full items-center justify-center gap-2">
                {row.map((image) => {
                  const imageIndex = imageList.findIndex((item) => item.id === image.id);
                  return (
                    <div
                      key={image.id}
                      className="group relative overflow-visible rounded-8 border border-system-white/16 bg-system-white04 shadow-[0px_3.648px_9.121px_0px_rgba(0,0,0,0.4)]"
                      style={{ width: `${previewCardWidth}px`, aspectRatio: '25 / 16' }}
                    >
                      {image.status === 'uploading' ? (
                        <div className="flex size-full items-center justify-center overflow-hidden rounded-[inherit]">
                          <Loading className="animate-spin text-2xl text-system-white" />
                        </div>
                      ) : image.status === 'done' && image.file ? (
                        <>
                          <div className="size-full overflow-hidden rounded-[inherit]">
                            <img src={image.file.selectedImagePreview} alt="" className="size-full object-contain" />
                          </div>
                          {showOrder ? (
                            <span className="absolute top-1.5 left-1.5 flex size-5 items-center justify-center rounded-full bg-black/65 font-medium text-[10px] text-system-white">
                              {imageIndex + 1}
                            </span>
                          ) : null}
                          <button
                            type="button"
                            className="absolute top-0 right-0 z-10 flex size-5 items-center justify-center rounded-full bg-black/55 text-system-white opacity-0 transition-all duration-200 hover:bg-black/75 group-hover:opacity-100"
                            style={{ transform: 'translate(50%, -50%)' }}
                            onClick={() => onRemoveImage(image.id)}
                          >
                            <BaseLineClose className="size-3" />
                          </button>
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : canAddMore ? (
          <ImageUploader
            maxCount={remainingImageSlots}
            maxFileSize={MAX_IMAGE_FILE_SIZE}
            getSelectedImage={getSelectedImage}
            onChange={onImageUpload}
            allowTempMode={true}
            imageAccept={imageAccept}
            className="group flex h-full w-full cursor-pointer items-center justify-center"
          >
            <div className="flex items-center justify-center gap-2 px-5 py-4">
              <BaseLineSubmit2 className="size-4 text-system-white" />
              <span className="text-system-white/40 text-xs leading-[1.4] group-hover:text-system-white">
                {t('tools.uploadNewFile')}
              </span>
            </div>
          </ImageUploader>
        ) : null}

        {hasImages && canAddMore ? (
          <ImageUploader
            maxCount={remainingImageSlots}
            maxFileSize={MAX_IMAGE_FILE_SIZE}
            getSelectedImage={getSelectedImage}
            onChange={onImageUpload}
            allowTempMode={true}
            imageAccept={imageAccept}
            className="absolute right-3 bottom-3 flex size-8 cursor-pointer items-center justify-center rounded-full border border-system-white/20 bg-black/40 text-system-white transition-colors hover:border-system-white/40 hover:bg-black/60"
          >
            <BaseLinePlus className="size-4" />
          </ImageUploader>
        ) : null}
      </div>
    </div>
  );

  if (!enableDrop) {
    return content;
  }

  return (
    <HeadlessImageUploader
      maxCount={remainingImageSlots}
      disabled={!canAddMore}
      maxFileSize={MAX_IMAGE_FILE_SIZE}
      getSelectedImage={getSelectedImage}
      onChange={onImageUpload}
      allowTempMode={true}
      imageAccept={imageAccept}
    >
      {({ uploadFiles }) => (
        <FileDropZone
          disabled={!canAddMore}
          onFilesDrop={(files) => {
            if (!files || files.length === 0) return;
            void uploadFiles(files);
          }}
        >
          {content}
        </FileDropZone>
      )}
    </HeadlessImageUploader>
  );
};
